import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import {
  ChevronLeft,
  Pencil,
  MapPin,
  Phone,
  Building2,
  Globe,
} from "lucide-react";
import { apiFetch } from "@/service/api";

interface Sucursal {
  id: number;
  nombre: string;
  direccion: string;
  ciudad: string;
  pais: string;
  telefono: string;
  estado: boolean;
}

export default function VerSucursal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sucursal, setSucursal] = useState<Sucursal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSucursal = async () => {
      try {
        const response = await apiFetch(`sucursales/find-one/${id}`);
        if (response.ok) {
          const data = await response.json();
          setSucursal(data);
        }
      } catch (error) {
        console.error("Error fetching sucursal:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSucursal();
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
      </div>
    );

  if (!sucursal) {
    return (
      <div className="text-center py-20">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
          Sucursal no encontrada
        </h3>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/multiempresa/sucursales")}
        >
          Volver al listado
        </Button>
      </div>
    );
  }

  const InfoCard = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string;
  }) => (
    <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-white/[0.02]">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-brand-500 shadow-sm dark:bg-gray-900">
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
          {value || "No especificado"}
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <PageBreadcrumb pageTitle="Detalle de Sucursal" />
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/multiempresa/sucursales")}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="size-5" />
          Volver al listado
        </button>
        <Button
          variant="primary"
          size="sm"
          startIcon={<Pencil className="size-4" />}
          onClick={() => navigate(`/multiempresa/sucursales/update/${id}`)}
        >
          Editar Sucursal
        </Button>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-8">
        <div className="flex flex-col items-center justify-center border-b border-gray-100 pb-8 dark:border-gray-800 sm:flex-row sm:justify-start sm:gap-8">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 text-3xl font-bold">
            <Building2 className="size-10" />
          </div>
          <div className="mt-4 text-center sm:mt-0 sm:text-left">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white/90 sm:text-3xl">
              {sucursal.nombre}
            </h2>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
              <Badge
                color={sucursal.estado ? "success" : "error"}
                variant="light"
              >
                {sucursal.estado ? "Activo" : "Inactivo"}
              </Badge>
            </div>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <InfoCard
            icon={<MapPin className="size-5" />}
            label="Direccion"
            value={sucursal.direccion}
          />
          <InfoCard
            icon={<MapPin className="size-5" />}
            label="Ciudad"
            value={sucursal.ciudad}
          />
          <InfoCard
            icon={<Globe className="size-5" />}
            label="Pais"
            value={sucursal.pais}
          />
          <InfoCard
            icon={<Phone className="size-5" />}
            label="Telefono"
            value={sucursal.telefono}
          />
        </div>
      </div>
    </div>
  );
}
